import React from 'react';
import messages from './messages';
import { FormattedMessage } from 'react-intl';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import { useFetchPaymentBatches } from './hooks';
import { formatDate, formatMoney } from '../../../utils/formatter';
import Spinner from '../../../components/Spinner';
import useIntl from '../../../hooks/useIntl';

type Props = {
  organizationId: string,
  propertyId: string,
  subsidyFilter: string,
  periodFilter: string,
  redirectToPaymentBatch: Function,
};

const renderPaymentBatchesRows = (
  paymentBatches,
  redirectToPaymentBatch,
  viewLabel,
) => {
  return paymentBatches && paymentBatches.length > 0 ? (
    paymentBatches.map((paymentBatch, i) => {
      const period = paymentBatch?.propertyFiscalPeriod?.period ?? '';
      const formattedPeriod = period
        ? `${period.substr(4)}/${period.substr(0, 4)}`
        : '';
      const isPosted = Boolean(paymentBatch?.isPosted);
      const isPosting = Boolean(paymentBatch?.isPosting);
      const isDeposited = paymentBatch?.depositStatus === 'Deposited';
      const status = isDeposited
        ? 'Deposited'
        : isPosting || isPosted
        ? 'Pending'
        : 'Open';
      const name = paymentBatch?.name ?? '';
      const date = paymentBatch?.batchDate ?? '';
      const amount = paymentBatch?.batchTotal ?? '';
      const documentNumber = paymentBatch?.documentNumber ?? '';
      const note = paymentBatch?.batchNote ?? '';
      const isSubsidyBatch = Boolean(paymentBatch?.isSubsidyBatch);
      return (
        <tr key={paymentBatch?.id || i}>
          <td>{name}</td>
          <td>{status}</td>
          <td>
            {isSubsidyBatch ? (
              <FormattedMessage {...messages.subsidy} />
            ) : (
              <FormattedMessage {...messages.resident} />
            )}
          </td>
          <td>{formatDate(date)}</td>
          <td>{isPosted ? formattedPeriod : 'Unposted'}</td>
          <td>{formatMoney(amount)}</td>
          <td>{documentNumber}</td>
          <td>{note}</td>
          <td>
            <ElementWithPermissions scope={['payment-batch-manage']}>
              <button
                className="btn btn-sm btn-default"
                aria-label={viewLabel}
                onClick={() => {
                  redirectToPaymentBatch(paymentBatch.id, isSubsidyBatch);
                }}
              >
                <i className="et-pencil" aria-hidden="true" />
              </button>
            </ElementWithPermissions>
          </td>
        </tr>
      );
    })
  ) : (
    <tr>
      <td colSpan={9}>
        <div className="text-center">
          <FormattedMessage {...messages.noDataDescription} />
        </div>
      </td>
    </tr>
  );
};

export const ManagePaymentBatchesDetails = (props: Props) => {
  const {
    organizationId,
    propertyId,
    subsidyFilter,
    periodFilter,
    redirectToPaymentBatch,
  } = props;
  const intl = useIntl();
  const viewLabel = intl.formatMessage(messages.view);
  const { paymentBatches, isLoading } = useFetchPaymentBatches({
    organizationId,
    propertyId,
    subsidyFilter,
    periodFilter,
  });

  return (
    <div className="container-fluid" data-test="payment-batches-details">
      <div className="table-scroll table-deposits-container">
        <table className="table table-prospects table-striped">
          <thead className="table-header">
            <tr>
              <th>
                <FormattedMessage {...messages.name} />
              </th>
              <th>
                <FormattedMessage {...messages.status} />
              </th>
              <th>
                <FormattedMessage {...messages.type} />
              </th>
              <th>
                <FormattedMessage {...messages.date} />
              </th>
              <th>
                <FormattedMessage {...messages.period} />
              </th>
              <th>
                <FormattedMessage {...messages.amount} />
              </th>
              <th>
                <FormattedMessage {...messages.doc} />
              </th>
              <th>
                <FormattedMessage {...messages.note} />
              </th>
              <th>
                <FormattedMessage {...messages.view} />
              </th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && paymentBatches ? (
              renderPaymentBatchesRows(
                paymentBatches,
                redirectToPaymentBatch,
                viewLabel,
              )
            ) : (
              <tr>
                <td colSpan={9}>
                  <div className="text-center">
                    <Spinner />
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="padtop30">
        <strong>
          <FormattedMessage {...messages.notes} />
        </strong>
        <div className="padtop5">
          <FormattedMessage {...messages.notes1} />
        </div>
        <div>
          <FormattedMessage {...messages.notes2} />
        </div>
      </div>
    </div>
  );
};

export default ManagePaymentBatchesDetails;
